import { createBrowserRouter, Navigate, useLocation, useParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { usePqrsdLabel, usePqrsdLabelReady } from "./components/pqrsd/pqrsdLabel";
import {
  DEFAULT_MODULE_SLUG,
  moduleSlug as moduleSlugFor,
} from "./services/backend/gfunctions/pqrsd/_shared/moduleLabel";
import LayoutMyTasks from "./components/layout/LayoutMyTasks";
import LayoutUpload from "./components/layout/LayoutUpload";
import LayoutPqrsdLab from "./components/layout/LayoutPqrsdLab";
import LayoutFiled from "./components/layout/LayoutFiled";
import LayoutFiledConfig from "./components/layout/LayoutFiledConfig";
import LayoutFiledHub from "./components/layout/LayoutFiledHub";
import LayoutFiledList from "./components/layout/LayoutFiledList";
import LayoutRequestsFiledList from "./components/layout/LayoutRequestsFiledList";
import LayoutHome from "./components/layout/LayoutHome";
import LayoutViewFiled from "./components/layout/LayoutViewFiled";
import LayoutCancelledFiled from "./components/layout/LayoutCancelledFiled";

import Login from "./components/login/Login";
import LayoutConfig from "./components/layout/LayoutConfig";
import PasswordReset from "./components/login/PasswordReset";
import ProtectedRoute from "./services/security/ProtectedRoute";
import LayoutProcesses from "./components/layout/LayoutProcesses";
import LayoutViewProcess from "./components/layout/LayoutViewProcess";
import LayoutViewProcessTemplate from "./components/layout/LayoutViewProcessTemplate";
import LayoutViewProcessesTemplate from "./components/layout/LayoutViewProcessesTemplate";
import LayoutViewFlow from "./components/layout/LayoutViewFlow";
import LayoutTask from "./components/layout/LayoutTask";
import LayoutUserProfile from "./components/layout/LayoutUserProfile";
import LayoutExpedients from "./components/layout/LayoutExpedients";
import LayoutMigration from "./components/layout/LayoutMigration";
import LayoutReports from "./components/layout/LayoutReports";
import LayoutContracts from "./components/layout/LayoutContracts";
import LayoutRichEditor from "./components/layout/LayoutRichEditor";
import LayoutSubmission from "./components/layout/LayoutSubmission";
import LayoutDocument from "./components/layout/LayoutDocument";
import LayoutPqrsd from "./components/layout/LayoutPqrsd";
import LayoutPqrsdLanding from "./components/layout/LayoutPqrsdLanding";
import LayoutPqrsdTrack from "./components/layout/LayoutPqrsdTrack";
import LayoutPqrsdAdmin from "./components/layout/LayoutPqrsdAdmin";
import LayoutPqrsdAdminDetail from "./components/layout/LayoutPqrsdAdminDetail";
import LayoutPqrsdMine from "./components/layout/LayoutPqrsdMine";
import LayoutPqrsdHub from "./components/layout/LayoutPqrsdHub";
import LayoutPqrsdWindow from "./components/layout/LayoutPqrsdWindow";
import LayoutPqrsdConfig from "./components/layout/LayoutPqrsdConfig";
import LayoutPqrsdTriage from "./components/layout/LayoutPqrsdTriage";
import LayoutPqrsdApprovals from "./components/layout/LayoutPqrsdApprovals";
import LayoutPqrsdGroup from "./components/layout/LayoutPqrsdGroup";
import LayoutPqrsdReports from "./components/layout/LayoutPqrsdReports";
import LayoutFinancing from "./components/layout/LayoutFinancing";
import LayoutAudit from "./components/layout/LayoutAudit";
import LayoutUserAdmin from "./components/layout/LayoutUserAdmin";
import LayoutUserReports from "./components/layout/LayoutUserReports";
import LayoutTrd from "./components/layout/LayoutTrd";
import LayoutTrdDependence from "./components/layout/LayoutTrdDependence";
import LayoutUsersHub from "./components/layout/LayoutUsersHub";
import LayoutExtensionHub from "./components/layout/LayoutExtensionHub";
import LayoutApplicantsHub from "./components/layout/LayoutApplicantsHub";
import LayoutAgreementsHub from "./components/layout/LayoutAgreementsHub";
import LayoutAgreements from "./components/layout/LayoutAgreements";
import LayoutAgreementView from "./components/layout/LayoutAgreementView";
import LayoutAgreementConfig from "./components/layout/LayoutAgreementConfig";
import LayoutEntities from "./components/layout/LayoutEntities";
import LayoutEntityView from "./components/layout/LayoutEntityView";
import LayoutMyEntity from "./components/layout/LayoutMyEntity";
import InviteAccept from "./components/myentity/InviteAccept";
import LayoutApplicants from "./components/layout/LayoutApplicants";
import LayoutApplicantView from "./components/layout/LayoutApplicantView";
import LayoutApplicantConfig from "./components/layout/LayoutApplicantConfig";
import LayoutContractConfig from "./components/layout/LayoutContractConfig";
import LayoutContractsLegal from "./components/layout/LayoutContractsLegal";
import LayoutPaymentRequests from "./components/layout/LayoutPaymentRequests";
import LayoutPaymentManagement from "./components/layout/LayoutPaymentManagement";
import AppErrorPage from "./components/generic/AppErrorPage";

// The citizen pages follow the module name an admin sets (Config → PQRSD →
// General): /pqrsd by default, /<slug> after a rename. They are routed as
// /:moduleSlug… and this guard decides: the current slug renders the page,
// "pqrsd" redirects to the current slug (query kept, so a magic link from an
// email sent just before a rename still opens the case), and anything else
// goes home — but only once the name is known, since on a first visit
// /<new-slug>/estado arrives before config/pqrsd does. Fixed paths (/home,
// /pqrsd/inicio, …) outrank these dynamic ones, so nothing is shadowed.
// Staff routes stay under /pqrsd.
function PqrsdPublicRoute({ page, children }) {
  const { moduleSlug } = useParams();
  const slug = moduleSlugFor(usePqrsdLabel());
  const ready = usePqrsdLabelReady();
  const { search, hash } = useLocation();
  if (moduleSlug === slug) return children;
  if (moduleSlug === DEFAULT_MODULE_SLUG) {
    return <Navigate to={`/${slug}${page ? `/${page}` : ""}${search}${hash}`} replace />;
  }
  if (!ready) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  return <Navigate to="/home" replace />;
}

const routes = [
  {
    path: "/",
    element: <Login />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/",
    element: <ProtectedRoute />,
    children: [
      {
        index: true,
        element: <Navigate to="/home" replace />,
      },
      {
        path: "/home",
        element: <LayoutHome />,
      },
      {
        path: "/processes",
        element: <LayoutProcesses />,
      },
    ],
  },
  {
    path: "/home",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/home",
        element: <LayoutHome />,
      },
    ],
  },
  {
    path: "/flow",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/flow/:flowId",
        element: <LayoutViewFlow />,
      },
    ],
  },
  {
    path: "/process",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/process/:processId",
        element: <LayoutViewProcess />,
      },
      {
        path: "/process/edit/:processId",
        element: <LayoutViewProcess editMode={true} />,
      },
    ],
  },
  {
    path: "/processTemplates",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/processTemplates/:processTemplateId",
        element: <LayoutViewProcessTemplate instance={true} />,
      },
    ],
  },
  {
    path: "/processesTemplate",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/processesTemplate",
        element: <LayoutViewProcessesTemplate />,
      },
      {
        path: "/processesTemplate/:processTemplateId",
        element: <LayoutViewProcessTemplate />,
      },
    ],
  },
  {
    path: "/expedients",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/expedients",
        element: <LayoutExpedients types={true} />,
      },
      {
        path: "/expedients/own",
        element: <LayoutExpedients own={true} />,
      },
      {
        path: "/expedients/:expedientTypeId",
        element: <LayoutExpedients />,
      },
      {
        path: "/expedients/document/:documentId",
        element: <LayoutDocument />,
      },
      {
        path: "/expedients/:expedientTypeId/:expedientId",
        element: <LayoutExpedients instance={true} />,
      },
    ],
  },
  {
    path: "/mytasks",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/mytasks",
        element: <LayoutMyTasks />,
      },
    ],
  },
  {
    path: "/task",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/task/:taskId",
        element: <LayoutTask />,
      },
    ],
  },
  {
    path: "/migration",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/migration",
        element: <LayoutMigration />,
      },
    ],
  },
  {
    path: "/filed",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/filed/:reference",
        element: <LayoutFiled />,
      },
      {
        path: "/filed/cancelled/:filedId",
        element: <LayoutCancelledFiled />,
      },
      {
        path: "/filed",
        element: <LayoutFiled />,
      },
    ],
  },
  {
    path: "/filed/view/:hash",
    element: <LayoutViewFiled />,
  },
  {
    path: "/filedlist",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/filedlist",
        element: <LayoutFiledList />,
      },
      {
        path: "/filedlist/requests",
        element: <LayoutRequestsFiledList />,
      },
      {
        path: "/filedlist/config",
        element: <LayoutFiledConfig />,
      },
      {
        path: "/filedlist/inicio",
        element: <LayoutFiledHub />,
      },
    ],
  },
  {
    path: "/reports",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/reports",
        element: <LayoutReports />,
      },
    ],
  },
  {
    path: "/contracts",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/contracts",
        element: <LayoutContracts list={true} />,
      },
      {
        path: "/contracts/closed",
        element: <LayoutContracts list={true} userType="closed" />,
      },
      {
        path: "/contracts/:contractId",
        element: <LayoutContracts />,
      },
      {
        path: "/contracts/new",
        element: <LayoutContracts newContract={true} />,
      },
      {
        // Contratos de personas jurídicas (colección contractsLegal).
        path: "/contracts/legal/new",
        element: <LayoutContractsLegal />,
      },
      {
        path: "/contracts/legal/:contractId",
        element: <LayoutContractsLegal />,
      },
      {
        path: "/contracts/legal/:contractId/view",
        element: <LayoutContractsLegal view={true} />,
      },
      {
        // Solicitudes de pago del contrato jurídico (C5). Rutas propias y no
        // una pestaña del contrato: la empresa entra aquí y no al formulario.
        path: "/contracts/legal/:contractCode/payments",
        element: <LayoutPaymentRequests />,
      },
      {
        path: "/contracts/legal/:contractCode/payments/:requestId",
        element: <LayoutPaymentRequests detail={true} />,
      },
      {
        path: "/contracts/supervisor",
        element: <LayoutContracts list={true} userType="supervisor" />,
      },
      {
        path: "/contracts/support",
        element: <LayoutContracts list={true} userType="support" />,
      },
      {
        path: "/contracts/contractor",
        element: <LayoutContracts list={true} userType="contractor" />,
      },
      {
        path: "/contracts/:contractId/view",
        element: <LayoutContracts view={true} />,
      },
      {
        path: "/contracts/:contractId/annex/:annexId",
        element: <LayoutContracts annex={true} />,
      },
      {
        path: "/contracts/consolidate",
        element: <LayoutContracts consolidate={true} />,
      },
      {
        // Configuración de catálogos del módulo (sólo ADMIN; el gate real está en el componente).
        path: "/contracts/config",
        element: <LayoutContractConfig />,
      },
    ],
  },
  {
    // Registro público de aspirantes / postulaciones (sin autenticación).
    path: "/submission",
    children: [
      {
        path: "/submission",
        element: <LayoutSubmission type="home" />,
      },
      {
        path: "/submission/new",
        element: <LayoutSubmission type="profile" />,
      },
      {
        // Reanudar postulación: enlace mágico (?t=) u OTP por correo/celular.
        path: "/submission/continuar",
        element: <LayoutSubmission type="continuar" />,
      },
      {
        // Registro público de entidades (empresas públicas y privadas). Cuelga
        // de /submission y no de una ruta propia porque es el mismo portal: la
        // portada las reparte en dos pestañas.
        path: "/submission/entidad/nueva",
        element: <LayoutSubmission type="entity-profile" />,
      },
      {
        // Ficha de la entidad: enlace mágico (?t=) u OTP por correo/celular.
        path: "/submission/entidad/continuar",
        element: <LayoutSubmission type="entity-continuar" />,
      },
    ],
  },
  {
    // Módulo Extensión — hub de dos niveles. El de arriba elige rama
    // (aspirantes / convenios) y cada rama tiene el suyo con sus pantallas: el
    // módulo creció y un solo mosaico ya no distinguía una cosa de la otra.
    path: "/extension",
    element: <ProtectedRoute />,
    children: [
      { path: "/extension/inicio", element: <LayoutExtensionHub /> },
      { path: "/extension/aspirantes", element: <LayoutApplicantsHub /> },
      { path: "/extension/convenios", element: <LayoutAgreementsHub /> },
    ],
  },
  {
    path: "/applicants",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/applicants",
        element: <LayoutApplicants />,
      },
      {
        // Configuración del módulo (sólo ADMIN; el gate real está en el componente).
        path: "/applicants/config",
        element: <LayoutApplicantConfig />,
      },
      {
        path: "/applicants/:nuip",
        element: <LayoutApplicantView />,
      },
    ],
  },
  {
    // Módulo Extensión — convenios (registro y presupuesto). El gate por
    // permiso está en los componentes, como en /applicants.
    path: "/agreements",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/agreements",
        element: <LayoutAgreements />,
      },
      {
        // Configuración del módulo (sólo ADMIN; el gate real está en el
        // componente). No colisiona con /agreements/:code: React Router ordena
        // por especificidad, y un segmento literal siempre gana sobre uno
        // dinámico, esté donde esté en la lista.
        path: "/agreements/config",
        element: <LayoutAgreementConfig />,
      },
      {
        path: "/agreements/:code",
        element: <LayoutAgreementView />,
      },
    ],
  },
  {
    // Módulo Extensión — entidades: las organizaciones con las que se firman los
    // convenios, con sus datos y su documentación. El gate por permiso está en
    // los componentes, como en /applicants y /agreements.
    path: "/entities",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/entities",
        element: <LayoutEntities />,
      },
      {
        path: "/entities/:nit",
        element: <LayoutEntityView />,
      },
    ],
  },
  {
    // The company's OWN record, for ENTITY users. The NIT is deliberately not in
    // the route: it comes from `entityNit` on the user document, so there is no
    // URL to tamper with to peek at another entity.
    path: "/my-entity",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/my-entity",
        element: <LayoutMyEntity />,
      },
    ],
  },
  {
    // Accepting an invitation. PUBLIC (outside ProtectedRoute, the /submission
    // pattern): whoever arrives has no account yet — creating it is the end of
    // this flow.
    path: "/invitation",
    element: <InviteAccept />,
  },
  {
    path: "/config",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/config",
        element: <LayoutConfig />,
      },
    ],
  },
  // Tabla de Retención Documental: independent of Expedientes, which only
  // reads what is published here.
  {
    path: "/trd",
    element: <ProtectedRoute />,
    children: [
      { path: "/trd", element: <LayoutTrd /> },
      { path: "/trd/:dependenceCode", element: <LayoutTrdDependence /> },
    ],
  },
  // Users module: a hub and its submodules, like the PQRSD one. Routes in
  // English; the page copy stays in Spanish.
  {
    path: "/users/home",
    element: <ProtectedRoute />,
    children: [{ path: "/users/home", element: <LayoutUsersHub /> }],
  },
  {
    path: "/users/admin",
    element: <ProtectedRoute />,
    children: [{ path: "/users/admin", element: <LayoutUserAdmin /> }],
  },
  {
    path: "/users/reports",
    element: <ProtectedRoute />,
    children: [{ path: "/users/reports", element: <LayoutUserReports /> }],
  },
  // The old user-admin address (the one on prod), kept for bookmarks.
  { path: "/admin", element: <Navigate to="/users/admin" replace /> },
  {
    path: "/labs",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/labs",
        element: <LayoutUpload />,
      },
      {
        path: "/labs/pqrsd",
        element: <LayoutPqrsdLab />,
      },
    ],
  },
  {
    path: "/password-reset",
    element: <PasswordReset />,
  },
  {
    path: "/:moduleSlug",
    element: <PqrsdPublicRoute page=""><LayoutPqrsdLanding /></PqrsdPublicRoute>,
  },
  {
    path: "/:moduleSlug/radicar",
    element: <PqrsdPublicRoute page="radicar"><LayoutPqrsd /></PqrsdPublicRoute>,
  },
  {
    path: "/:moduleSlug/estado",
    element: <PqrsdPublicRoute page="estado"><LayoutPqrsdTrack /></PqrsdPublicRoute>,
  },
  {
    path: "/pqrsd/inicio",
    element: <ProtectedRoute />,
    children: [{ path: "/pqrsd/inicio", element: <LayoutPqrsdHub /> }],
  },
  {
    path: "/pqrsd/ventanilla",
    element: <ProtectedRoute />,
    children: [{ path: "/pqrsd/ventanilla", element: <LayoutPqrsdWindow /> }],
  },
  {
    path: "/pqrsd/admin",
    element: <ProtectedRoute />,
    children: [
      { path: "/pqrsd/admin", element: <LayoutPqrsdAdmin /> },
      { path: "/pqrsd/admin/:radicado", element: <LayoutPqrsdAdminDetail /> },
    ],
  },
  {
    path: "/pqrsd/mine",
    element: <ProtectedRoute />,
    children: [
      { path: "/pqrsd/mine", element: <LayoutPqrsdMine /> },
      { path: "/pqrsd/mine/:radicado", element: <LayoutPqrsdAdminDetail /> },
    ],
  },
  {
    path: "/pqrsd/config",
    element: <ProtectedRoute />,
    children: [{ path: "/pqrsd/config", element: <LayoutPqrsdConfig /> }],
  },
  {
    path: "/pqrsd/informes",
    element: <ProtectedRoute />,
    children: [{ path: "/pqrsd/informes", element: <LayoutPqrsdReports /> }],
  },
  {
    // Route in English like the rest; the label the user sees is
    // "Casos a revisar".
    path: "/pqrsd/approvals",
    element: <ProtectedRoute />,
    children: [{ path: "/pqrsd/approvals", element: <LayoutPqrsdApprovals /> }],
  },
  {
    path: "/pqrsd/triage",
    element: <ProtectedRoute />,
    children: [
      { path: "/pqrsd/triage", element: <LayoutPqrsdTriage /> },
      { path: "/pqrsd/triage/:radicado", element: <LayoutPqrsdAdminDetail /> },
    ],
  },
  {
    path: "/pqrsd/dependencia",
    element: <ProtectedRoute />,
    children: [
      { path: "/pqrsd/dependencia", element: <LayoutPqrsdGroup /> },
      { path: "/pqrsd/dependencia/:radicado", element: <LayoutPqrsdAdminDetail /> },
    ],
  },
  {
    path: "/profile",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/profile",
        element: <LayoutUserProfile />,
      },
    ],
  },
  {
    path: "/richeditor",
    element: <LayoutRichEditor />,
  },
  {
    path: "*",
    element: <Navigate to="/home" replace />,
  },
  {
    path: "/financing",
    element: <ProtectedRoute />,
    children: [{ path: "/financing", element: <LayoutFinancing /> }],
  },
  {
    // Gestión de Pagos: recorrido financiero de las facturas jurídicas
    // (legal-payment-financial-flow). El gate por permiso (payment_flow.list)
    // lo hace la vista, igual que /financing.
    path: "/payments/management",
    element: <ProtectedRoute />,
    children: [
      { path: "/payments/management", element: <LayoutPaymentManagement /> },
      { path: "/payments/management/:requestId", element: <LayoutPaymentManagement detail={true} /> },
    ],
  },
  {
    path: "/audit",
    element: <ProtectedRoute />,
    children: [{ path: "/audit", element: <LayoutAudit /> }],
  },
];

// Every top-level route gets the same fallback, so a throw anywhere in the
// tree renders our page instead of react-router's English default.
export const router = createBrowserRouter(
  routes.map((route) => ({ errorElement: <AppErrorPage />, ...route }))
);
